import { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Lock, Mail, User as UserIcon, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { IMAGES } from "@/data/site";
import Reveal from "@/components/Reveal";

export default function Login() {
  const { login, signup, isAuthed } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const redirect = new URLSearchParams(loc.search).get("redirect") || "/";

  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);

  if (isAuthed) return <Navigate to={redirect} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !form.name.trim()) { toast.error("Please enter your name."); return; }
    if (!form.email.trim() || !form.password) { toast.error("Email and password are required."); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }

    const res = mode === "login" ? await login(form) : await signup(form);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success(mode === "login" ? "Welcome back!" : "Account created. Welcome!");
    nav(redirect, { replace: true });
  };

  return (
    <div data-testid="page-login" className="bg-[var(--bg)] pt-[110px] md:pt-[120px] pb-20 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-4 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
        {/* Left visual */}
        <Reveal className="hidden lg:block lg:col-span-6">
          <div className="relative h-full min-h-[560px] rounded-2xl overflow-hidden">
            <img src={IMAGES.milletGrains || IMAGES.farmer} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[var(--green-dark)]/85 via-[var(--green)]/70 to-transparent" />
            <div className="relative h-full p-10 flex flex-col justify-end text-white">
              <div className="overline text-[var(--gold-soft)]">Native Touch · Pantry</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight font-semibold">
                Wholesome by nature. <br />
                <span className="text-[var(--gold)] italic">Modern by design.</span>
              </h2>
              <p className="mt-5 max-w-md text-white/85 leading-relaxed">
                Sign in to track orders, save favourites and reorder your everyday millet essentials in a tap.
              </p>
              <div className="h-[3px] w-16 bg-[var(--gold)] mt-7 rounded-full" />
            </div>
          </div>
        </Reveal>

        {/* Right form */}
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="card-soft p-7 md:p-10">
            <div className="overline overline-green">{mode === "login" ? "Welcome Back" : "Join Native Touch"}</div>
            <h1 className="font-serif-display text-3xl md:text-5xl mt-3 leading-tight font-semibold text-[var(--ink)]">
              {mode === "login" ? "Sign in to your pantry." : "Create your account."}
            </h1>

            <div className="mt-7 inline-flex rounded-full bg-[var(--bg-cream)] p-1 border border-[var(--line-soft)]">
              {[
                { id: "login", label: "Sign In" },
                { id: "signup", label: "Sign Up" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`auth-tab-${t.id}`}
                  onClick={() => setMode(t.id)}
                  className={`px-5 py-2 text-xs uppercase tracking-widest font-semibold rounded-full transition ${
                    mode === t.id ? "bg-[var(--green)] text-white" : "text-[var(--ink-2)] hover:text-[var(--green)]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={onSubmit} data-testid="auth-form" className="mt-7 space-y-4">
              {mode === "signup" && (
                <Field testId="auth-name" icon={UserIcon} label="Full name" value={form.name}
                       onChange={(v) => setForm({ ...form, name: v })} placeholder="Your name" />
              )}
              <Field testId="auth-email" icon={Mail} label="Email" type="email" value={form.email}
                     onChange={(v) => setForm({ ...form, email: v })} placeholder="you@email.com" />
              <Field testId="auth-password" icon={Lock} label="Password"
                     type={showPw ? "text" : "password"}
                     value={form.password}
                     onChange={(v) => setForm({ ...form, password: v })}
                     placeholder="At least 6 characters"
                     trailing={
                       <button type="button" onClick={() => setShowPw((v) => !v)} aria-label="Toggle password" className="text-[var(--ink-3)] hover:text-[var(--green)]">
                         {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                       </button>
                     }
              />
              <button type="submit" data-testid="auth-submit" className="btn-primary w-full !py-3 mt-2">
                {mode === "login" ? "Sign In" : "Create Account"} <ArrowRight size={14} />
              </button>
            </form>

            <p className="kbd text-[0.6rem] text-[var(--ink-3)] text-center mt-5">
              {mode === "login" ? (
                <>New here? <button data-testid="auth-switch-signup" onClick={() => setMode("signup")} className="text-[var(--green)] font-semibold link-underline">Create an account</button></>
              ) : (
                <>Already have an account? <button data-testid="auth-switch-login" onClick={() => setMode("login")} className="text-[var(--green)] font-semibold link-underline">Sign in</button></>
              )}
            </p>

            <p className="text-[0.65rem] text-center text-[var(--ink-3)] mt-3">
              By continuing you agree to our <Link to="/contact" className="underline">Terms</Link> & <Link to="/contact" className="underline">Privacy Policy</Link>.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

function Field({ testId, icon: Icon, label, value, onChange, type = "text", placeholder, trailing }) {
  return (
    <div className="flex flex-col">
      <label className="kbd text-[var(--ink-3)] mb-1.5 text-[0.6rem]">{label}</label>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
        <input
          data-testid={testId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white border border-[var(--line)] rounded-md pl-10 pr-10 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
        />
        {trailing && <div className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</div>}
      </div>
    </div>
  );
}
